import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { Component as SizeFilter } from '../SizeFilter';

const TriggerClear = styled.button`
  outline: none;
  cursor: pointer;

  ${props => props.theme.subHeader.variants.subHeading4Uppercase.textStyles}
  color: ${props =>
    props.selectedProductSize
      ? props.theme.colors.gunmetal
      : props.theme.colors.grey};
`;

const FilterReviewsWrapper = styled.div`
  display: flex;
  position: relative;
  max-width: 100%;
  width: 272px;
`;

const StyledSizeFilter = styled(SizeFilter)`
  display: flex;
  flex: 1;

  & ${SizeFilter.FilterLabel} {
    display: none;
  }

  & ${SizeFilter.StyledFilterFlyout.Button} {
    flex: 1;
    outline: none;
  }

  & ${SizeFilter.StyledFilterFlyout.Button}[aria-expanded='true'] {
    color: ${props => props.theme.colors.gunmetal};
    background-color: ${props => props.theme.colors.white};
  }

  & ${SizeFilter.StyledFilterFlyout.Popup} {
    min-width: 343px;
    box-shadow: 0 0 0;
    border-top: 0;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
    left: -1px;
    padding: 11px 24px 24px;
    top: calc(100% - 2px);
    z-index: 10;

    @media (max-width: ${props => props.theme.breakpoints.medium - 1}px) {
      min-width: 311px;
    }
  }

  & ${SizeFilter.SizeSectionWrap} {
    justify-content: center;
  }

  & ${SizeFilter.SizeCategoryWrapper} {
    margin-bottom: 0;

    ${SizeFilter.BraCategoryLabel} {
      color: ${props => props.theme.colors.gunmetal};
      text-align: center;
      ${props => props.theme.paragraph.variants.paragraph4Uppercase.textStyles}
    }

    ${SizeFilter.BraCategoryLabel}:nth-of-type(4) {
      margin-top: 32px;
    }
  }

  & ${SizeFilter.SizeCheckbox} {
    margin: 6px;

    > ${SizeFilter.SizeCheckbox.InputWrapper} {
      ${SizeFilter.SizeCheckbox.Label} {
        top: 11px;
        font-weight: normal;
      }

      ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        background-color: ${props => props.theme.colors.white};
        border: 1px solid ${props => props.theme.colors.stone};
        width: 45px;
        height: 45px;
      }

      input:checked + ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        background-color: ${props => props.theme.colors.black};
        border-color: ${props => props.theme.colors.stone};
        padding: 0;
      }

      input:checked ~ ${SizeFilter.SizeCheckbox.Label} {
        color: ${props => props.theme.colors.white};
        top: 11px;
      }

      input:active
        + ${SizeFilter.SizeCheckbox.CustomCheckbox},
        input:focus
        + ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        border: 1px solid ${props => props.theme.colors.stone};
      }
    }
  }

  & ${SizeFilter.CupSizeCheckbox} {
    margin: 6px;

    > ${SizeFilter.CupSizeCheckbox.InputWrapper} {
      ${SizeFilter.CupSizeCheckbox.Label} {
        top: 14px;
        font-weight: normal;
      }

      ${SizeFilter.CupSizeCheckbox.CustomCheckbox} {
        background-color: ${props => props.theme.colors.white};
        border: 1px solid ${props => props.theme.colors.stone};
        width: 45px;
        height: 45px;
      }

      input:checked + ${SizeFilter.CupSizeCheckbox.CustomCheckbox} {
        background-color: ${props => props.theme.colors.black};
        border-color: ${props => props.theme.colors.stone};
        padding: 0;
      }

      input:checked ~ ${SizeFilter.CupSizeCheckbox.Label} {
        color: ${props => props.theme.colors.white};
        top: 14px;
      }
    }

    ${SizeFilter.BandLabel} {
      display: none;
    }
  }
`;

const CTAButton = styled.span`
  display: inline-block;
  margin: 0 4px 0 ${props => props.theme.spacing.tiny}px;

  ${props => props.theme.subHeader.variants.subHeading4Uppercase.textStyles}
  color: ${props => props.theme.colors.gunmetal};
`;

const SelectedSize = styled.span`
  font-weight: bold;
`;

function FilterReviews({
  isBra,
  isBralette,
  isLingerie,
  isMen,
  isSleep,
  isUndies,
  onChange,
  selectedProductSize,
  sizes,
}) {
  const handleClearClick = useCallback(
    e => {
      e.preventDefault();
      if (selectedProductSize) {
        onChange(null);
      }
    },
    [onChange, selectedProductSize]
  );

  const handleChooseSize = useCallback(
    (sizeId, sizeField, newProductSize) => {
      onChange({ sizeId, sizeField, newProductSize });
    },
    [onChange]
  );

  const formatted = sizes
    .map(size => ({
      id: size,
      label: size,
    }))
    .filter(_ => _);

  const formattedSizes = {
    bra: isBra ? formatted : null,
    bralette: isBralette ? formatted : null,
    lingerie: isLingerie || isSleep ? formatted : null,
    men: isMen ? formatted : null,
    undies: isUndies ? formatted : null,
  };

  const selectedSizes = {
    braSizes: isBra ? [selectedProductSize] : [],
    braletteSizes: isBralette ? [selectedProductSize] : [],
    lingerieSizes: isLingerie || isSleep ? [selectedProductSize] : [],
    menSizes: isMen ? [selectedProductSize] : [],
    undieSizes: isUndies ? [selectedProductSize] : [],
    optionSignatures: [],
  };

  const handleGenerateFilterCount = useCallback(() => null, []);

  if (!isBra && !isBralette && !isLingerie && !isUndies && !isSleep && !isMen) {
    return null;
  }

  return (
    <FilterReviewsWrapper isOpen={false}>
      <StyledSizeFilter
        label={
          <CTAButton data-autotag="filterBtn">
            <FormattedMessage
              id="site_pdp.size_filter_cta"
              defaultMessage="Size Filter"
            />
            {selectedProductSize && (
              <>
                {': '}
                <SelectedSize>{selectedProductSize}</SelectedSize>
              </>
            )}
          </CTAButton>
        }
        braSizes={formattedSizes.bra}
        braletteSizes={formattedSizes.bralette}
        lingerieSizes={formattedSizes.lingerie}
        menSizes={formattedSizes.men}
        undieSizes={formattedSizes.undies}
        sleepSizes={formattedSizes.sleep}
        selectedSizes={selectedSizes}
        onChooseSize={handleChooseSize}
        generateFilterCount={handleGenerateFilterCount}
      />
      <TriggerClear
        onClick={handleClearClick}
        selectedProductSize={selectedProductSize}
      >
        <FormattedMessage
          id="site_pdp.clear_filter_cta"
          defaultMessage="Clear"
        />
      </TriggerClear>
    </FilterReviewsWrapper>
  );
}

FilterReviews.propTypes = {
  isBra: PropTypes.bool,
  isBralette: PropTypes.bool,
  isLingerie: PropTypes.bool,
  isMen: PropTypes.bool,
  isSleep: PropTypes.bool,
  isUndies: PropTypes.bool,
  onChange: PropTypes.func,
  selectedProductSize: PropTypes.string,
  sizes: PropTypes.array,
};

FilterReviews.defaultProps = {
  sizes: [],
  onChange: () => null,
};

export default FilterReviews;
